import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, fmtDate, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle2, XCircle, PackageCheck, ChevronRight, History as HistoryIcon } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  delivered: "default", cancelled: "destructive", returned: "destructive",
  refunded: "destructive", failed: "destructive",
};

export default function History() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    api.get("/orders/assigned-to-me?limit=100")
      .then((r) => setRows((r.data.items || []).filter((o) => ["delivered", "cancelled", "returned", "refunded"].includes(o.status))))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (tab === "delivered") return rows.filter((o) => o.status === "delivered");
    if (tab === "failed") return rows.filter((o) => ["cancelled", "returned", "refunded"].includes(o.status));
    return rows;
  }, [rows, tab]);

  return (
    <div className="p-4" data-testid="delivery-history-page">
      <div className="mb-4">
        <h1 className="text-2xl font-display font-semibold">History</h1>
        <p className="text-xs text-muted-foreground mt-1">Past deliveries assigned to you</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-3">
        <TabsList className="w-full">
          <TabsTrigger value="all" className="flex-1" data-testid="history-tab-all">All</TabsTrigger>
          <TabsTrigger value="delivered" className="flex-1" data-testid="history-tab-delivered">Delivered</TabsTrigger>
          <TabsTrigger value="failed" className="flex-1" data-testid="history-tab-failed">Failed</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading && Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full mb-2" />)}

      {!loading && filtered.length === 0 && (
        <Card className="card-soft border-transparent" data-testid="history-empty">
          <CardContent className="p-8 text-center">
            <HistoryIcon className="w-10 h-10 mx-auto text-muted-foreground opacity-40 mb-2" />
            <div className="font-medium">Nothing here yet</div>
            <div className="text-sm text-muted-foreground">Your completed deliveries will appear here.</div>
          </CardContent>
        </Card>
      )}

      {filtered.map((o) => (
        <Link key={o.id} to={`/delivery/order/${o.id}`} data-testid={`history-row-${o.order_number}`}>
          <Card className="card-soft border-transparent mb-2 hover:shadow-md transition">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${o.status === "delivered" ? "bg-accent/15 text-accent" : "bg-destructive/10 text-destructive"}`}>
                {o.status === "delivered" ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-mono font-semibold text-sm">{o.order_number}</div>
                  <Badge variant={STATUS_TONE[o.status] || "secondary"} className="capitalize text-[10px]">{o.status?.replace(/_/g, " ")}</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                  {o.address?.city} · {o.address?.pincode} · {money(o.pricing?.total)}
                </div>
                <div className="text-[10px] text-muted-foreground">{fmtDate(o.delivery?.delivered_at || o.updated_at)}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}
